import { GzTitleWebComponent } from '../../../ui/gzTitle';
import { GenericGridSmartViewsWebComponent } from '../../../grid/genericGridSmartViews';
import { GzPushBodyLayoutWebComponent } from '../../../ui/gzPushBodyLayout';
import { GridViewWebComponent } from '../../../grid/gridView';
import { Column, ColumnKeys, EasmArtifactsInventoryGridViewData } from '../types';
import {
  WEB_COMPONENT_GENERIC_GRID_SMART_VIEWS,
  WEB_COMPONENT_GRID_VIEW,
  WEB_COMPONENT_GZ_PUSH_BODY_LAYOUT,
} from '../../../webcomponents.constants';
import { convertSmartViewToGridView, SmartViewItemConfiguration } from '../../../grid/genericGridSmartViews/types';
import { ColumnDetails } from '../../../grid/types/common';
import { EasmArtifactsInventoryGridDataProvider } from '../dataProvider/EasmArtifactsInventoryGridDataProvider';
import { GridTitleConfig } from '../../../grid/uiElements/gridViewTitle/types';
import _ from 'lodash';
import {
  artifactCVEsRenderer,
  artifactNameRenderer,
  companyRenderer,
  isVulnerableRenderer,
  portRenderer,
  relatedAssetsRenderer,
} from './customRenderers';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { dateRenderer } from '@nirvana/shared/common';
import { GzTextWebComponent } from '../../../ui/gzText';
import { FAVORITE_ICON_STATE } from '../../../grid/utils/constants';
import { ViewType } from '../../../grid/gridSmartViews/types';
import { ViewConfiguration } from '../../../grid/gridView/types';
import { GzEASMSideMenuWebComponent } from '../../easmSideMenu';
import { easmIconSet } from '../../../theme/easm-icons';
import { GroupType } from '../../../ui/gzEnumList/types';
import { EasmInventoryBaseGridView } from '../../assetsInventoryGrid/view/EasmInventoryBaseGridView';
import { GzImageRepoWebComponent } from '../../../ui/gzImageRepo';
import { GzBackendError } from '../../../accounts/common/types/GzBackendError';
import { htmlEncode } from '../../../utils/escape.utils';
import { translateFn } from '../../../utils/translator.helper';
import { ErrorCodes } from '../../types';
import {
  investigationStatusCellRenderer,
  assigneeCellRenderer,
  priorityCellRenderer,
  notesCellRenderer,
  changePropertyActionsRenderer,
} from '../../utils/utils';
import { GzSelectMenuWithButtonWebComponent } from '../../../ui/gzSelectMenuWithButton';
import { MAX_NUMBER_OF_FINDINGS_TO_BE_SELECTED } from '../../types';

export class EasmArtifactsInventoryGridView extends EasmInventoryBaseGridView {
  public dom: {
    $title: GzTitleWebComponent;
    $detailsPanel: GzEASMSideMenuWebComponent;
    $pushBodyLayout: GzPushBodyLayoutWebComponent;
    $smartViewsTitle: GzTitleWebComponent;
    $smartViews: GenericGridSmartViewsWebComponent;
    $smartViewsLoader: HTMLElement;
    $gridWrapper: HTMLElement;
    $innerGridWrapper: HTMLElement;
    $gridView: GridViewWebComponent<Column, ColumnKeys>;
    $scanConfigurationBtn: GzButtonWebComponent;
    $changeInvestigationStatusBtn: GzSelectMenuWithButtonWebComponent;
    $assignBtn: GzSelectMenuWithButtonWebComponent;
    $prioritizeBtn: GzSelectMenuWithButtonWebComponent;
    $noResultAdditionalMessage: GzTextWebComponent;
    $noEligibleTargets: GzImageRepoWebComponent;
    $noEligibleTargetsMessage: GzTextWebComponent;
  };

  private allColumns: Array<ColumnDetails<Column, ColumnKeys>>;
  private predefinedFilterKeys: Array<ColumnKeys>;

  public setTranslatedStrings(): void {
    this.smartViewsStrings = this.stringsTranslator.getSmartViewsTranslations();
    this.translations = this.stringsTranslator.getEASMStrings();
    this.dom.$smartViewsTitle.innerHTML = this.smartViewsStrings['genericPanelTitle'].toUpperCase();
    this.dom.$scanConfigurationBtn.innerHTML = this.translations['scanConfiguration'].toUpperCase();
    this.dom.$changeInvestigationStatusBtn.btnText = this.translations['changeInvestigationStatus'].toUpperCase();
    this.dom.$assignBtn.btnText = this.translations['assign'].toUpperCase();
    this.dom.$prioritizeBtn.btnText = this.translations['prioritize'].toUpperCase();
    this.dom.$title.innerText = this.translations.easmArtifactsInventoryPageTitle;
    this.dom.$noEligibleTargetsMessage.innerText = this.translations.noEligibleTargetsMessage;
  }

  public async getDomElements(): Promise<void> {
    this.dom.$pushBodyLayout = this.shadow.querySelector(WEB_COMPONENT_GZ_PUSH_BODY_LAYOUT);
    this.dom.$smartViewsTitle = this.shadow.querySelector('#smart-views-title');
    this.dom.$smartViews = this.shadow.querySelector(WEB_COMPONENT_GENERIC_GRID_SMART_VIEWS);
    this.dom.$smartViewsLoader = this.shadow.querySelector('.smart-views-loader');
    this.dom.$gridWrapper = this.shadow.querySelector('.wrapper');
    this.dom.$innerGridWrapper = this.shadow.querySelector('.grid');
    this.dom.$gridView = this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW);
    this.dom.$scanConfigurationBtn = this.shadow.querySelector('#scan-configuration-button');
    this.dom.$changeInvestigationStatusBtn = this.shadow.querySelector('#change-investigation-status-button');
    this.dom.$assignBtn = this.shadow.querySelector('#assign-button');
    this.dom.$prioritizeBtn = this.shadow.querySelector('#prioritize-button');
    this.dom.$detailsPanel = this.shadow.querySelector('gz-easm-side-menu');
    this.dom.$title = this.shadow.querySelector('#title');
    this.dom.$noEligibleTargets = this.shadow.querySelector('#no-eligible-targets');
    this.dom.$noEligibleTargetsMessage = this.shadow.querySelector('#no-eligible-targets-message');

    this.dom.$noResultAdditionalMessage = this.shadow.querySelector('#no-results-additional-message');
    this.setTranslatedStrings();
  }

  public async buildSuccessView(data: EasmArtifactsInventoryGridViewData): Promise<void> {
    if (await this.hasCompaniesWithEASM()) {
      this.dom.$noEligibleTargets.classList.add('hidden');
      this.dom.$pushBodyLayout.classList.remove('hidden');
    } else {
      this.dom.$pushBodyLayout.classList.add('hidden');
      this.dom.$noEligibleTargets.classList.remove('hidden');
    }

    this.allColumns = data.allColumns;
    this.predefinedFilterKeys = data.predefinedFilterKeys;
    this.investigationStatusOptions = data.investigationStatusOptions;
    this.assigneeOptions = data.assigneeOptions;
    this.priorityOptions = data.priorityOptions;

    this.resetTemplateState(this.dom.$gridWrapper);

    await this.buildSmartViews(data.smartViewsCategories);
    await this.buildGridView(this.dom.$smartViews.controller?.getActiveView() as SmartViewItemConfiguration);
    this.dom.$detailsPanel.injectDependencies(this.dependencies);
  }

  public async buildGridView(smartView: SmartViewItemConfiguration): Promise<void> {
    this.dom.$gridView.propertiesSet = false;

    this.dom.$gridView.dataProvider = new EasmArtifactsInventoryGridDataProvider();

    const gridView = convertSmartViewToGridView<Column, ColumnKeys>(smartView);

    //New artifacts is only a filter not a column
    gridView.displayedColumns = gridView.displayedColumns.filter(column => column !== Column.NEW_ARTIFACTS);
    this.dom.$gridView.favoriteIconConfig = this.configureFavoriteIcon(gridView);
    if (this.dependencies.permissions.canHaveChildCompanies) {
      if (!gridView.filters.some(filter => filter.hasOwnProperty(ColumnKeys.COMPANY))) {
        gridView.filters.unshift({
          [ColumnKeys.COMPANY]: { exactMatch: this.dependencies.getActiveCompany().companyId },
        } as any);
      }
      const gridViewColumns = gridView.displayedColumns;
      gridView.displayedColumns = [...gridViewColumns.slice(0, 2), Column.COMPANY, ...gridViewColumns.slice(2)];
    }

    const initialGridView = _.cloneDeep(gridView);

    this.dom.$gridView.titleConfig = new GridTitleConfig(gridView.name);
    this.dom.$gridView.allColumnDetails = this.allColumns;
    this.dom.$gridView.enableCSVExport = true;
    this.dom.$gridView.columnRenderers = {
      [ColumnKeys.CERTIFICATE_REPORTS_VALID_FROM]: dateRenderer.bind(this),
      [ColumnKeys.CERTIFICATE_REPORTS_VALID_UNTIL]: dateRenderer.bind(this),
      [ColumnKeys.COMPANY]: companyRenderer.bind(this),
      [ColumnKeys.VULNERABLE_TO_ZONE_TRANSFER]: isVulnerableRenderer.bind(this),
      [ColumnKeys.NAME]: artifactNameRenderer.bind(this),
      [ColumnKeys.SERVICES_CVES]: artifactCVEsRenderer.bind(this),
      [ColumnKeys.SERVICES_PORT]: portRenderer.bind(this),
      [ColumnKeys.ASSETS_RELATED_TO_ARTIFACT]: relatedAssetsRenderer.bind(this),
      [ColumnKeys.INVESTIGATION_STATUS]: investigationStatusCellRenderer.bind(this),
      [ColumnKeys.ASSIGNEE]: assigneeCellRenderer.bind(this),
      [ColumnKeys.PRIORITY]: priorityCellRenderer.bind(this),
      [ColumnKeys.NOTES]: notesCellRenderer.bind(this),
      [ColumnKeys.CHANGE_PROPERTY_ACTIONS]: changePropertyActionsRenderer.bind(this),
    };
    this.dom.$gridView.columns = gridView.displayedColumns;
    this.dom.$gridView.columnConfigs = gridView.columnConfigs ?? [];
    this.dom.$gridView.filters = gridView.filters;
    this.addPivotingFilterValues();
    this.dom.$gridView.hasSelectableRows = true;
    this.dom.$gridView.selectionStoreConfig = {
      maxNumOfItems: MAX_NUMBER_OF_FINDINGS_TO_BE_SELECTED,
    };
    this.dom.$gridView.predefinedFiltersKeys = this.predefinedFilterKeys;
    this.dom.$gridView.viewId = gridView.id;
    this.dom.$gridView.defaultViewProps = {
      ...initialGridView,
      gridSettingsColumnsGroupType: GroupType.ACCORDION,
    };
    this.dom.$gridView.activeViewProps = gridView;
    this.dom.$gridView.activeViewInitialProps = initialGridView;
    this.dom.$gridView.isCompactViewChecked = gridView.compactView;
    this.dom.$gridView.hasPagination = true;
    this.dom.$gridView.pageSize = gridView.pageSize;
    this.dom.$gridView.showGlobalActions = true;
    this.dom.$gridView.smartViewsEnabled = true;
    this.dom.$gridView.customStrings = {};
    //@ts-ignore
    this.dom.$gridView.filtersUiConfigs = this.getFiltersUiConfig();
    this.dom.$gridView.propertiesSet = true;
    this.dom.$gridView.onDataLoadingFinishedWithError = error => this.setGridErrorMessage(error);

    this.dom.$gridView.enableRowClickEvent = true;
    this.dom.$gridView.customStrings = {
      noSearchResultsString: this.translations['noResultMsgTitleArtifacts'],
    };

    this.dom.$changeInvestigationStatusBtn.filterOptions = this.investigationStatusOptions;
    this.dom.$assignBtn.filterOptions = this.assigneeOptions;
    this.dom.$prioritizeBtn.filterOptions = this.priorityOptions;
    this.dom.$noResultAdditionalMessage.innerHTML = this.translations['noResultMsgDescription'];

    this.dom.$gridView.injectDependencies(this.dependencies);

    await this.dom.$gridView.start();
  }

  public async setGridErrorMessage(error: GzBackendError | undefined) {
    let dataLoadingFailedMessage = htmlEncode(
      translateFn(
        'Data loading has failed. Please try to refresh the page and if the issue persists contact Bitdefender Enterprise Support for more information.'
      )
    );

    if (error?.err?.code === ErrorCodes.FEATURE_NOT_AVAILABLE_FOR_COMPANY) {
      dataLoadingFailedMessage = translateFn(
        'The company must have a valid EASM license.'
      );
    }

    this.dom.$gridView.customStrings.dataLoadingFailed = dataLoadingFailedMessage;
    this.dom.$gridView.controller?.updateCustomStrings();
  }

  private configureFavoriteIcon(viewConfiguration: ViewConfiguration<Column, ColumnKeys>): Object {
    const favoriteIconConfig = {
      icon: FAVORITE_ICON_STATE.OUTLINE,
      disabled: true,
      active: false,
    };
    if (viewConfiguration.type === ViewType.DEFAULT) {
      favoriteIconConfig.icon = FAVORITE_ICON_STATE.OUTLINE;
      favoriteIconConfig.disabled = true;
      favoriteIconConfig.active = false;
      return favoriteIconConfig;
    }
    if (viewConfiguration.isFavorite) {
      favoriteIconConfig.icon = FAVORITE_ICON_STATE.FILLED;
      favoriteIconConfig.disabled = false;
      favoriteIconConfig.active = true;
      return favoriteIconConfig;
    }

    favoriteIconConfig.disabled = false;
    return favoriteIconConfig;
  }

  private getFiltersUiConfig() {
    return [
      this.getCompanyFilterUiConfig(ColumnKeys.COMPANY),
      {
        key: ColumnKeys.SERVICES_PORT,
        config: {
          min: 0,
          max: 65535,
        },
      },
      {
        key: ColumnKeys.NAME,
        config: {
          iconSet: easmIconSet,
        },
      },
      {
        key: ColumnKeys.ASSETS_RELATED_TO_ARTIFACT,
        config: {
          iconSet: easmIconSet,
        },
      },
    ];
  }
}
